import "../../Tiptap.scss";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import { Button } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useFormikContext } from "formik";
import "./styles.css";

const RewriteModal = ({
  label,
  previous,
  save,
  rewritten,
  rewriteFieldName,
  setOpenModal,
  partTitle = null,
  isSaveDisabled = false, 
}) => {
  const { t } = useTranslation();

  const oldEditorRef = useRef(null);
  const newEditorRef = useRef(null);

  return (
    <div className="m-auto bg-white rounded-xl w-[80%] max-w-[900px] h-[95%] max-h-[600px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex justify-between p-4 items-center border-b-[3px] border-slate-200">
        <p className="font-semibold">{label}</p>
        <Close
          onClick={() => setOpenModal(false)}
          sx={{ fontSize: 32, cursor: "pointer" }}
        />
      </div>

      {/* Editors */}
      <div className="p-4 overflow-y-auto overflow-x-hidden justify-stretch items-stretch grid grid-cols-12 gap-4 h-100">
        {/* OLD VERSION */}
        <div className="relative col-span-12 xl:col-span-6 border-b border-r-0 xl:border-r xl:border-b-0 pr-2 h-100">
          <div className="flex items-center mb-4 justify-between">
            <p className="mb-2">{t("old", "Ancien")}</p>
            <Button
              sx={{ textTransform: "none" }}
              onClick={() => save(oldEditorRef.current?.editor.getHTML())}
              variant="contained"
              disabled={isSaveDisabled} //  disable during generation
            >
                {t("save-old", "Save old version")}

              
            </Button>
          </div>

          <div className="rewrite-editor">
            <Editor content={previous} ref={oldEditorRef} rewriteFieldName={""} />
          </div>
        </div>

        {/* NEW VERSION */}
        <div className="relative col-span-12 xl:col-span-6">
          <div className="flex items-center mb-4 justify-between">
            <p className="mb-2">{t("new", "Nouveau")}</p>
            <Button
              sx={{ textTransform: "none" }}
              onClick={() => save(newEditorRef.current?.editor.getHTML())}
              variant="contained"
              disabled={isSaveDisabled} // disable during generation
            >
             
               { t("save-new", "Sauvegarder le nouveau")}
            </Button>
          </div>

          <div className="rewrite-editor">
            <Editor
              content={rewritten}
              ref={newEditorRef}
              rewriteFieldName={rewriteFieldName}
              partTitle={partTitle}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Editor = forwardRef(
  ({ content, rewriteFieldName, partTitle = null }, ref) => {
    const formikProps = useFormikContext();

    const editor = useEditor({
      extensions: [
        StarterKit,
        Link.configure({
          openOnClick: false,
          autolink: true,
        }),
        Table.configure({
          resizable: true,
          allowTableNodeSelection: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
      ],
      content: content || "",
      onUpdate: ({ editor }) => {
        if (rewriteFieldName.includes("rewrite")) {
          if (rewriteFieldName === "part_rewrite") {
            formikProps.setFieldValue("content_rewrite", {
              ...formikProps.values.content_rewrite,
              [partTitle]: editor.view.dom.innerHTML,
            });
          } else {
            formikProps.setFieldValue(
              rewriteFieldName,
              editor.view.dom.innerHTML
            );
          }
        }
      },
    });

    useEffect(() => {
      if (editor && content !== editor.getHTML()) {
        editor.commands.setContent(content?.replace(/(<br\s*\/?>)+$/i, ""));
      }
    }, [content, editor]);

    useEffect(() => {
      if (ref) ref.current = { editor };
    }, [editor, ref]);

    return editor ? <EditorContent editor={editor} /> : null;
  }
);

export default RewriteModal;
